import { z } from 'zod'
import { noticeSeveritySchema } from './content.js'

/**
 * Grade de horarios da paroquia.
 *
 * Modelada como recorrencia semanal (`dayOfWeek` + `time`) mais excecoes por
 * data civil. Isso resolve o caso que o usuario destacou como central: a
 * alteracao repentina de um horario, sem reescrever a grade inteira e sem
 * perder o registro de que aquela semana foi diferente.
 */

export const scheduleKindSchema = z.enum([
  'MISSA',
  'CONFISSAO',
  'ADORACAO',
  'TERCO',
  'NOVENA',
  'OUTRO',
])

export type ScheduleKind = z.infer<typeof scheduleKindSchema>

export const SCHEDULE_KIND_LABELS: Record<ScheduleKind, string> = {
  MISSA: 'Missa',
  CONFISSAO: 'Confissoes',
  ADORACAO: 'Adoracao ao Santissimo',
  TERCO: 'Terco',
  NOVENA: 'Novena',
  OUTRO: 'Outro',
}

/** Hora de parede no formato HH:mm - 24 horas, sempre com dois digitos. */
export const wallClockSchema = z
  .string()
  .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Informe a hora no formato HH:mm')

export const massScheduleInputSchema = z.object({
  kind: scheduleKindSchema.default('MISSA'),
  dayOfWeek: z.coerce.number().int().min(0, 'Dia invalido').max(6, 'Dia invalido'),
  time: wallClockSchema,
  /** Igreja matriz, capela, comunidade rural. */
  location: z.string().min(2, 'Informe o local').max(120).trim(),
  /** "Missa das criancas", "Com a Pastoral da Crianca". */
  note: z.string().max(160).trim().optional(),
  active: z.boolean().default(true),
  /** Ordem de exibicao entre horarios do mesmo dia e hora. */
  sortOrder: z.coerce.number().int().min(0).default(0),
})

export type MassScheduleInput = z.infer<typeof massScheduleInputSchema>

/**
 * Excecao numa data especifica. `scheduleId` nulo aplica a excecao a TODOS os
 * horarios daquela data - o caso de fechar a igreja por um dia.
 */
export const massScheduleExceptionInputSchema = z
  .object({
    scheduleId: z.string().nullable().optional(),
    date: z.iso.date('Informe a data'),
    cancelled: z.boolean().default(false),
    newTime: wallClockSchema.nullable().optional(),
    newLocation: z.string().max(120).trim().nullable().optional(),
    reason: z.string().min(3, 'Explique o motivo da alteracao').max(200).trim(),
  })
  .superRefine((data, ctx) => {
    if (!data.cancelled && !data.newTime && !data.newLocation) {
      ctx.addIssue({
        code: 'custom',
        path: ['newTime'],
        message: 'Informe o novo horario, o novo local, ou marque como cancelado',
      })
    }
    if (data.cancelled && (data.newTime || data.newLocation)) {
      ctx.addIssue({
        code: 'custom',
        path: ['cancelled'],
        message: 'Um horario cancelado nao pode ter novo horario ou local',
      })
    }
    if (data.scheduleId == null && !data.cancelled) {
      ctx.addIssue({
        code: 'custom',
        path: ['scheduleId'],
        message: 'Excecao para todos os horarios do dia so pode ser cancelamento',
      })
    }
  })

export type MassScheduleExceptionInput = z.infer<typeof massScheduleExceptionInputSchema>

/**
 * Acao combinada "alterar horario e avisar a comunidade".
 *
 * Existe porque o fluxo real e um so: quando o horario muda, a comunidade
 * precisa saber AGORA. Separar em duas telas garantiria que, na correria, a
 * excecao fosse lancada e o aviso esquecido.
 */
export const announceScheduleChangeSchema = z.object({
  exception: massScheduleExceptionInputSchema,
  announce: z.boolean().default(true),
  noticeSeverity: noticeSeveritySchema.default('URGENTE'),
  /** Vazio: a API monta a mensagem a partir da excecao. */
  noticeMessage: z.string().max(280).trim().optional(),
  /** Dias de permanencia da faixa na home. */
  noticeDurationDays: z.coerce.number().int().min(1).max(30).default(7),
})

export type AnnounceScheduleChangeInput = z.infer<typeof announceScheduleChangeSchema>

/** Horario ja resolvido (grade + excecao) para uma data - o que o portal exibe. */
export interface ResolvedScheduleSlot {
  scheduleId: string
  kind: ScheduleKind
  date: string
  time: string
  location: string
  note: string | null
  cancelled: boolean
  /** Diferente da grade normal: a interface marca como alterado. */
  changed: boolean
  changeReason: string | null
  originalTime: string | null
}
